// src/Tracking/GroupTracking.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import './GroupTracking.scss';
import api from '../Axios/axios';
import { FaPencilAlt, FaArrowRight, FaListUl } from 'react-icons/fa';

/* ------------------------------------------------------------------ */
/* 1) OWNER ID listeleri (Excel'den gelen CompanyId'leri buraya yapıştır)
/* ------------------------------------------------------------------ */
const normalizeCompanyName = (value) =>
  (value || '')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c');

const GROUP_TRACKING_CACHE_KEY = 'groupTrackingCacheV2';

const readGroupTrackingCache = () => {
  try {
    return JSON.parse(sessionStorage.getItem(GROUP_TRACKING_CACHE_KEY) || '{}');
  } catch (error) {
    console.error('Group tracking cache okunamadi:', error);
    return {};
  }
};

const writeGroupTrackingCache = (value) => {
  try {
    sessionStorage.setItem(GROUP_TRACKING_CACHE_KEY, JSON.stringify(value));
  } catch (error) {
    console.error('Group tracking cache yazilamadi:', error);
  }
};

const OPEN_TECHNICAL_SERVICE_STATUSES = new Set([1, 2, 3, 4, 5]);

const normalizeTechnicalServiceStatus = (status) => {
  if (typeof status === 'number') return status;

  const statusMap = {
    New: 1,
    Assigned: 2,
    InProgress: 3,
    CompletedByPersonnel: 4,
    WaitingManagerApproval: 5,
    Approved: 6,
    SentToAccounting: 7,
    Cancelled: 8
  };

  if (typeof status === 'string') {
    if (statusMap[status]) return statusMap[status];
    const parsed = Number(status);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  return 0;
};

const GroupTracking = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStatus = searchParams.get('status');
  const routeStatus = ['Inused', 'Active', 'Reserved', 'TechnicalService'].includes(requestedStatus) ? requestedStatus : 'all';
  const initialCache = readGroupTrackingCache();

  const [towerCranes, setTowerCranes] = useState(() => initialCache.towerCranes || []);
  const [companies, setCompanies] = useState(() => initialCache.companies || []);
  const [brands, setBrands] = useState(() => initialCache.brands || []);
  const [warehouses, setWarehouses] = useState(() => initialCache.warehouses || []);
  const [rentals, setRentals] = useState(() => initialCache.rentals || []);
  const [technicalServiceRequests, setTechnicalServiceRequests] = useState([]);
  const [ownerId, setOwnerId] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [reserves, setReserves] = useState(() => initialCache.reserves || []);

  const [filters, setFilters] = useState({
    serialNumber: '',
    owner: '',
    brand: '',
    model: '',
    height: '',
    tonnage: '',
    status: routeStatus,
    rentalCompany: '',
    workSite: '',
  });

  useEffect(() => {
    setFilters(previous => ({ ...previous, status: routeStatus }));
  }, [routeStatus]);

  const getUserFromToken = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    const ownerId = decodedToken.CompanyId;
    const role = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    setOwnerId(ownerId);
    setUserRole(role);
  };

  const fetchAllRentals = async () => {
    try {
      const rentalResponse = await api.get('/Rental', {
        params: { serviceStatus: 'Approved', includeLineItems: false }
      });
      setRentals(rentalResponse.data.data || []);
    } catch (error) {
      console.error('Kiralama bilgileri yüklenemedi:', error);
    }
  };

  const fetchAllReserves = async () => {
    try {
      const reserveResponse = await api.get('/Reserve');
      setReserves(reserveResponse.data.data || []);
    } catch (error) {
      console.error('Rezerve verisi alınamadı:', error);
    }
  };

  const fetchTechnicalServiceRequests = async () => {
    try {
      const response = await api.get('/TechnicalService/requests');
      setTechnicalServiceRequests(response?.data?.data || []);
    } catch (error) {
      console.error('Teknik servis talepleri yüklenemedi:', error);
      setTechnicalServiceRequests([]);
    }
  };

  useEffect(() => {
    getUserFromToken();

    api.get('/TowerCrane')
      .then((response) => {
        setTowerCranes(response?.data?.data || []);
      })
      .catch((error) => {
        console.error('Vinç listesi yüklenemedi:', error);
      });

    api.get('/Company')
      .then((response) => {
        setCompanies(response?.data?.data || []);
      })
      .catch((error) => {
        console.error('Firma listesi yüklenemedi:', error);
      });

    api.get('/Brand')
      .then((response) => {
        setBrands(response?.data?.data || []);
      })
      .catch((error) => {
        console.error('Marka listesi yüklenemedi:', error);
      });

    api.get('/Warehouse')
      .then((response) => {
        setWarehouses(response?.data?.data || []);
      })
      .catch((error) => {
        console.error('Depo listesi yüklenemedi:', error);
      });

    fetchAllRentals();
    fetchAllReserves();
    fetchTechnicalServiceRequests();
  }, []);

  useEffect(() => {
    writeGroupTrackingCache({
      towerCranes,
      companies,
      brands,
      warehouses,
      rentals,
      technicalServiceRequests,
      reserves
    });
  }, [towerCranes, companies, brands, warehouses, rentals, technicalServiceRequests, reserves]);


  const handleFilterChange = (e, filterName) => {
    setFilters({ ...filters, [filterName]: e.target.value });
    if (filterName === 'status') {
      const next = new URLSearchParams(searchParams);
      if (e.target.value === 'all') next.delete('status');
      else next.set('status', e.target.value);
      setSearchParams(next);
    }
  };

  // Tarih yardımcıları
  function formatTrDate(isoDateString) {
    if (!isoDateString) return '-';
    const dateObj = new Date(isoDateString);
    if (isNaN(dateObj.getTime())) return '-';
    const day = dateObj.getDate();
    const month = dateObj.getMonth() + 1;
    const year = dateObj.getFullYear();
    const dayStr = day < 10 ? '0' + day : '' + day;
    const monthStr = month < 10 ? '0' + month : '' + month;
    return `${dayStr}.${monthStr}.${year}`;
  }
  function isManualRentalActive(crane) {
    if (!crane?.isManualRented) return false;
    return true;
  }
  function isManualReservationActive(crane) {
    if (!crane?.isManualReserved) return false;
    return true;
  }

  const getCompanyName = (ownerId) => {
    const company = companies.find((company) => company.id === ownerId);
    return company ? company.name : '-';
  };
  const getBrandName = (brandId) => {
    const brand = brands.find((brand) => brand.id === brandId);
    return brand ? brand.name : '-';
  };

  const activeRentalMap = useMemo(() => {
    const map = new Map();
    rentals.forEach((rental) => {
      if (rental?.towerCraneId && rental?.isActive) {
        const existingRental = map.get(rental.towerCraneId);
        const existingStart = existingRental?.rentalStartDate ? new Date(existingRental.rentalStartDate).getTime() : 0;
        const currentStart = rental?.rentalStartDate ? new Date(rental.rentalStartDate).getTime() : 0;

        if (!existingRental || currentStart >= existingStart) {
          map.set(rental.towerCraneId, rental);
        }
      }
    });
    return map;
  }, [rentals]);

  const reservedMap = useMemo(() => {
    const map = new Map();
    reserves.forEach((reserve) => {
      if (reserve?.towerCraneId && reserve?.reserveType?.toLowerCase() === 'reserved') {
        map.set(reserve.towerCraneId, reserve);
      }
    });
    return map;
  }, [reserves]);

  const openTechnicalServiceCraneIds = useMemo(() => {
    const ids = new Set();
    technicalServiceRequests.forEach((request) => {
      const craneId = Number(request?.towerCraneId);
      const status = normalizeTechnicalServiceStatus(request?.status);
      if (craneId > 0 && OPEN_TECHNICAL_SERVICE_STATUSES.has(status)) {
        ids.add(craneId);
      }
    });
    return ids;
  }, [technicalServiceRequests]);

  // Kiralama/Rezerv detayları
  const getRentalDetails = (crane) => {
    const siteAddress = crane.isInWarehouse ? 'DEPODA' : (crane.currentSiteAddress || '-');
    const activeRental = activeRentalMap.get(crane.id);
    if (activeRental) {
      const rp  = parseInt(activeRental.rentalPeriod, 10) || 0;
      const ep  = parseInt(activeRental.extraPeriod, 10) || 0;
      const epm = parseInt(activeRental.extraPeriodMonth, 10) || 0;

      const extensionMonths = epm;
      const extensionDays = ep;
      const totalMonths = rp + extensionMonths;

      let durStr = '';
      if (totalMonths > 0) durStr += `${totalMonths} ay`;
      if (extensionDays > 0) durStr += (durStr ? ' ' : '') + `${extensionDays} gün`;
      if (!durStr) durStr = '-';

      return {
        startDate:      formatTrDate(activeRental.rentalStartDate),
        endDate:        formatTrDate(activeRental.rentalEndDate),
        fee:            activeRental.rent ? `${activeRental.rent} TL` : '-',
        rentalDuration: durStr,
        workSite:       activeRental.workSite || '-',
        status:         'Inused',
        rentalCompany:  activeRental.rentingCompany || '-',
        siteAddress,
        rentalId:       activeRental.id,
      };
    }

    if (isManualRentalActive(crane)) {
      const startDate = crane.manualRentalStartDate ? new Date(crane.manualRentalStartDate) : null;
      const endDate = crane.manualRentalEndDate ? new Date(crane.manualRentalEndDate) : null;
      let duration = '-';
      if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const monthDiff =
          (endDate.getFullYear() - startDate.getFullYear()) * 12 +
          (endDate.getMonth() - startDate.getMonth()) +
          1;
        duration = `${Math.max(monthDiff, 1)} ay`;
      }

      return {
        startDate: formatTrDate(crane.manualRentalStartDate),
        endDate: formatTrDate(crane.manualRentalEndDate),
        fee: crane.manualRentalFee ? `${crane.manualRentalFee} TL` : '-',
        rentalDuration: duration,
        workSite: crane.currentSiteName || crane.warehouse || '-',
        status: 'Inused',
        rentalCompany: crane.manualRentalCompany || '-',
        siteAddress,
        rentalId: null,
      };
    }

    if (crane.status === 'Reserved') {
      const reserve = reservedMap.get(crane.id);
      return {
        startDate: reserve ? formatTrDate(reserve.rentalStartDate) : '-',
        endDate:   reserve ? formatTrDate(reserve.rentalEndDate)   : '-',
        fee:       reserve?.rent ? `${reserve.rent} TL` : '-',
        rentalDuration: reserve ? `${reserve.rentalPeriod || 0} ay` : '-',
        workSite:  reserve?.workSite || crane.warehouse || '-',
        status:    'Reserved',
        rentalCompany: reserve?.rentingCompany || '-',
        siteAddress,
        rentalId:  null,
      };
    }

    if (isManualReservationActive(crane)) {
      const startDate = crane.manualReserveStartDate ? new Date(crane.manualReserveStartDate) : null;
      const endDate = crane.manualReserveEndDate ? new Date(crane.manualReserveEndDate) : null;
      let duration = '-';
      if (startDate && endDate && !isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        const monthDiff =
          (endDate.getFullYear() - startDate.getFullYear()) * 12 +
          (endDate.getMonth() - startDate.getMonth()) +
          1;
        duration = `${Math.max(monthDiff, 1)} ay`;
      }

      return {
        startDate: formatTrDate(crane.manualReserveStartDate),
        endDate: formatTrDate(crane.manualReserveEndDate),
        fee: '-',
        rentalDuration: duration,
        workSite: crane.currentSiteName || crane.warehouse || '-',
        status: 'Reserved',
        rentalCompany: crane.manualReserveCompany || '-',
        siteAddress,
        rentalId: null,
      };
    }

    if (crane.hasOpenTechnicalService || openTechnicalServiceCraneIds.has(Number(crane.id))) {
      return {
        startDate: '-',
        endDate: '-',
        fee: '-',
        rentalDuration: '-',
        workSite: crane.currentSiteName || crane.warehouse || '-',
        status: 'TechnicalService',
        rentalCompany: '-',
        siteAddress,
        rentalId: null,
      };
    }

    return {
      startDate: '-',
      endDate: '-',
      fee: '-',
      rentalDuration: '-',
      workSite: crane.currentSiteName || crane.warehouse || '-',
      status: 'Active',
      rentalCompany: '-',
      siteAddress,
      rentalId: null,
    };
  };

  // FİLTRELEME
  const filteredTowerCranes = towerCranes
    .filter((crane) => {
      // 1) Kullanıcı Company ise kendi makineleri
      if (userRole === 'Company' && crane.ownerId !== ownerId) return false;

      const rentalDetails = getRentalDetails(crane);

      const matchesSerialNumber = !filters.serialNumber || crane.serialNo.toLowerCase().includes(filters.serialNumber.toLowerCase());
      const matchesOwner = !filters.owner || crane.ownerId === filters.owner;
      const matchesBrand = !filters.brand || crane.brandId.toString() === filters.brand;
      const matchesModel = !filters.model || crane.model === filters.model;
      const matchesHeight = !filters.height || crane.height.toString() === filters.height;
      const matchesTonnage = !filters.tonnage || crane.maxTonnage.toString() === filters.tonnage;
      const matchesStatus = filters.status === 'all' || rentalDetails.status === filters.status;
      const matchesRentalCompany = !filters.rentalCompany || rentalDetails.rentalCompany === filters.rentalCompany;
      const matchesWorkSite = !filters.workSite || rentalDetails.workSite === filters.workSite;

      return (
        matchesSerialNumber &&
        matchesOwner &&
        matchesBrand &&
        matchesModel &&
        matchesHeight &&
        matchesTonnage &&
        matchesStatus &&
        matchesRentalCompany &&
        matchesWorkSite
      );
    });

  // SIRALAMA
  const sortedTowerCranes = [...filteredTowerCranes].sort((a, b) => {
    const ownerA = normalizeCompanyName(getCompanyName(a.ownerId));
    const ownerB = normalizeCompanyName(getCompanyName(b.ownerId));
    const ownerCompare = ownerA.localeCompare(ownerB, 'tr-TR');
    if (ownerCompare !== 0) return ownerCompare;

    return String(a.serialNo || '').localeCompare(String(b.serialNo || ''), 'tr-TR', {
      numeric: true,
      sensitivity: 'base',
    });
  });

  // Unique değerler (filtre dropdownları için)
  const uniqueBrands = Array.from(new Set(sortedTowerCranes.map((crane) => crane.brandId)));
  const uniqueOwners = Array.from(new Set(sortedTowerCranes.map((crane) => crane.ownerId)));
  const uniqueModels = Array.from(new Set(sortedTowerCranes.map((crane) => crane.model)));
  const uniqueHeights = Array.from(new Set(sortedTowerCranes.map((crane) => crane.height)));
  const uniqueTonnages = Array.from(new Set(sortedTowerCranes.map((crane) => crane.maxTonnage)));
  const uniqueRentalCompanies = Array.from(new Set(sortedTowerCranes.map((crane) => getRentalDetails(crane).rentalCompany)));
  const uniqueWorkSites = Array.from(new Set(sortedTowerCranes.map((crane) => getRentalDetails(crane).workSite)));
  const canManageRentalOperations = userRole === 'Owner' || userRole === 'Employee' || userRole === 'RentalPersonnel';

  const handleCraneEdit = (crane) => navigate(`/towercraneupdate?craneId=${crane.id}`);
  const handleRentalPage = (crane) => navigate(`/rentalpage/${crane.id}`, { state: { crane } });
  const handleSendToRental = (crane) =>
    navigate(`/cranerental?craneId=${crane.id}`, {
      state: {
        selectedCraneId: crane.id,
        selectedCrane: crane
      }
    });
  const handleReserveEdit = (reserve, crane) =>
    navigate(`/reserveedit/${reserve.id}`, {
      state: {
        reserve,
        crane
      }
    });

  function getStatusClass(status) {
    if (status === 'Inused') {
      return 'row-used';
    }
    if (status === 'Reserved') return 'row-reserve';
    if (status === 'TechnicalService') return 'row-technical-service';
    return 'row-active';
  }

  return (
    <div className="tracking-page">
      <Navbar />

{/* ÜST ARAÇ ÇUBUĞU */}
<div className="tracking-toolbar">
  <div className="table-legend">
    <div className="legend-item"><span className="legend-circle used"></span> Kirada</div>
    <div className="legend-item"><span className="legend-circle reserve"></span> Rezerve</div>
    <div className="legend-item"><span className="legend-circle technical-service"></span> Teknik Serviste</div>
    <div className="legend-item"><span className="legend-circle active"></span> {'Bo\u015fta'}</div>
  </div>
</div>

      <div className="table-container">
        <table>
                              <thead>
            <tr>
              <th>No</th>
              <th>{'Kule Vin\u00e7 Sahibi'}</th>
              <th>{'Seri Numaras\u0131'}</th>
              <th>Marka</th>
              <th>Model</th>
              <th>{'Y\u00fckseklik'}</th>
              <th>Tonaj</th>
              <th>Durum</th>
              <th>{'Kira Ba\u015flang\u0131\u00e7 Tarihi'}</th>
              <th>{'Kira Biti\u015f Tarihi'}</th>
              <th>Kiralanan Firma</th>
              <th>{'\u015eantiye/Depo'}</th>
              {canManageRentalOperations && <th>{'\u0130\u015flemler'}</th>}
            </tr>
            <tr>
              <th></th>
              <th>
                <div className="select-container">
                  <select
                    value={filters.owner}
                    onChange={(e) => handleFilterChange(e, 'owner')}
                  >
                    <option value="">{'T\u00fcm\u00fc'}</option>
                    {uniqueOwners.map((owner, index) => (
                      <option key={index} value={owner}>
                        {getCompanyName(owner)}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              <th>
                <div className="input-container serial-filter">
                  <input
                    type="text"
                    value={filters.serialNumber}
                    placeholder="Seri No Ara"
                    onChange={(e) => handleFilterChange(e, 'serialNumber')}
                  />
                </div>
              </th>
              <th>
                <div className="select-container">
                  <select
                    value={filters.brand}
                    onChange={(e) => handleFilterChange(e, 'brand')}
                  >
                    <option value="">{'T\u00fcm\u00fc'}</option>
                    {uniqueBrands.map((brand, index) => (
                      <option key={index} value={brand}>
                        {getBrandName(brand)}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              <th>
                <div className="select-container">
                  <select
                    value={filters.model}
                    onChange={(e) => handleFilterChange(e, 'model')}
                  >
                    <option value="">{'T\u00fcm\u00fc'}</option>
                    {uniqueModels.map((model, index) => (
                      <option key={index} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              <th>
                <div className="select-container">
                  <select
                    value={filters.height}
                    onChange={(e) => handleFilterChange(e, 'height')}
                  >
                    <option value="">{'T\u00fcm\u00fc'}</option>
                    {uniqueHeights.map((height, index) => (
                      <option key={index} value={height}>
                        {height}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              <th>
                <div className="select-container">
                  <select
                    value={filters.tonnage}
                    onChange={(e) => handleFilterChange(e, 'tonnage')}
                  >
                    <option value="">{'T\u00fcm\u00fc'}</option>
                    {uniqueTonnages.map((tonnage, index) => (
                      <option key={index} value={tonnage}>
                        {tonnage}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              <th>
                <div className="select-container">
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange(e, 'status')}
                  >
                    <option value="all">{'T\u00fcm\u00fc'}</option>
                    <option value="Active">{'Bo\u015fta'}</option>
                    <option value="Inused">Kirada</option>
                    <option value="Reserved">Rezerve</option>
                    <option value="TechnicalService">Teknik Serviste</option>
                  </select>
                </div>
              </th>
              <th></th><th></th>
              <th>
                <div className="select-container">
                  <select
                    value={filters.rentalCompany}
                    onChange={(e) => handleFilterChange(e, 'rentalCompany')}
                  >
                    <option value="">{'T\u00fcm\u00fc'}</option>
                    {uniqueRentalCompanies.map((company, index) => (
                      <option key={index} value={company}>
                        {company}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              <th>
                <div className="select-container">
                  <select
                    value={filters.workSite}
                    onChange={(e) => handleFilterChange(e, 'workSite')}
                  >
                    <option value="">{'T\u00fcm\u00fc'}</option>
                    {uniqueWorkSites.map((site, index) => (
                      <option key={index} value={site}>
                        {site}
                      </option>
                    ))}
                  </select>
                </div>
              </th>
              {canManageRentalOperations && <th></th>}
            </tr>
          </thead>

          <tbody>
            {sortedTowerCranes.map((crane, index) => {
              const rentalDetails = getRentalDetails(crane);
              const rowClass = getStatusClass(rentalDetails.status);
              const reserve = reserves.find(r => r.towerCraneId === crane.id);

              return (
                <tr key={crane.id} className={rowClass}>
                  <td>{index + 1}</td>
                  <td>{getCompanyName(crane.ownerId)}</td>
                  <td>{crane.serialNo}</td>
                  <td>{getBrandName(crane.brandId)}</td>
                  <td>{crane.model || '-'}</td>
                  <td>{crane.height}m</td>
                  <td>{crane.maxTonnage}T</td>
                  <td>
                    {rentalDetails.status === 'Inused' ? 'Kirada'
                      : rentalDetails.status === 'Reserved' ? 'Rezerve'
                      : rentalDetails.status === 'TechnicalService' ? 'Teknik Serviste'
                      : 'Bo\u015fta'}
                  </td>
                  <td>{rentalDetails.startDate}</td>
                  <td>{rentalDetails.endDate}</td>
                  <td>{rentalDetails.rentalCompany}</td>
                  <td>{rentalDetails.workSite || '-'}</td>
                  {canManageRentalOperations && (
                    <td>
                      <div className="btn-container">
                        {rentalDetails.status === 'Active' ? (
                          <button className="small-btn rental-action-btn" title="Kiraya gönder" onClick={() => handleSendToRental(crane)}>
                            <FaArrowRight />
                          </button>
                        ) : rentalDetails.status === 'Reserved' && reserve ? (
                          <button
                            className="small-btn edit-btn"
                            title="Rezervasyonu düzenle"
                            onClick={() => {
                              if (reserve) handleReserveEdit(reserve, crane);
                            }}
                          >
                            <FaPencilAlt />
                          </button>
                        ) : (
                          <span className="action-slot-placeholder" aria-hidden="true" />
                        )}
                        <button className="small-btn details-btn" title="Detay" onClick={() => handleRentalPage(crane)}>
                          <FaListUl />
                        </button>
                        <button className="small-btn edit-btn" title={'Vin\u00e7 bilgilerini d\u00fczenle'} onClick={() => handleCraneEdit(crane)}>
                          <FaPencilAlt />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>
    </div>
  );
};

export default GroupTracking;


